import { metricColors } from "components/custom/analytics/palettes";
import { getFormatter, type MetricConfig } from "views/merchandise/metrics";
import { METRIC_GROUPING_LOOKUP } from "views/merchandise/tables/metricsByEntity";

const IMPRESSIONS_AXIS = 2;
const CLICK_AXIS = 3;
const UNITS_AXIS = 4;
const BID_AXIS = 5;
const SALES_AXIS = 6;
const PERCENT_AXIS = 7;
const ROI_AXIS = 8;
const RATE_AXIS = 15;

export const metrics: MetricConfig[] = [
  {
    id: "dsp_total_sales",
    name: "Total Sales",
    color: metricColors.sales[0],
    accessor: (d) => d?.total_sales,
    prev_accessor: (d) => d?.total_sales_prev,
    accessorId: "total_sales",
    format: "currency",
    axis: SALES_AXIS,
    toolTip: "The total sales",
  },
  {
    id: "dsp_total_sales_percent",
    name: "Total Sales %",
    color: metricColors.sales[0],
    accessor: (d) => d?.total_sales_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "dsp_total_sales_delta",
    name: "Sales (+/-)",
    color: metricColors.sales[0],
    accessor: (d) => d?.total_sales_delta,
    format: "currency",
    incremental: true,
    axis: SALES_AXIS,
  },
  {
    id: "dsp_sales",
    name: "Sales",
    color: metricColors.sales[4],
    accessor: (d) => d?.sales,
    prev_accessor: (d) => d?.sales_prev,
    accessorId: "sales",
    format: "currency",
    axis: SALES_AXIS,
    toolTip: "Sales of promoted products, attributed to an ad view or click",
  },
  {
    id: "dsp_sales_percent",
    name: "Sales %",
    color: metricColors.sales[4],
    accessor: (d) => d?.sales_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "dsp_sales_delta",
    name: "Sales (+/-)",
    color: metricColors.sales[4],
    accessor: (d) => d?.sales_delta,
    format: "currency",
    incremental: true,
    axis: SALES_AXIS,
  },

  {
    id: "dsp_impressions",
    name: "Impressions",
    color: metricColors.metric1[3],
    accessor: (d) => d?.impressions ?? 0,
    prev_accessor: (d) => d?.impressions_prev ?? 0,
    format: "number",
    chartType: "column",
    axis: IMPRESSIONS_AXIS,
    toolTip: "The number of times the ad was displayed, excluding general invalid traffic or more",
  },
  {
    id: "dsp_impressions_percent",
    name: "Impressions %",
    color: metricColors.metric1[3],
    accessor: (d) => d?.impressions_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "dsp_clicks",
    name: "Clicks",
    color: metricColors.metric1[0],
    accessor: (d) => d?.clicks ?? 0,
    prev_accessor: (d) => d?.clicks_prev ?? 0,
    chartType: "column",
    format: "number",
    axis: CLICK_AXIS,
    toolTip: "The total clicks on ads",
  },
  {
    id: "dsp_clicks_percent",
    name: "Clicks %",
    color: metricColors.metric1[0],
    accessor: (d) => d?.clicks_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "dsp_cost",
    name: "Cost",
    color: metricColors.cost[0],
    accessor: (d) => d?.total_cost,
    prev_accessor: (d) => d?.total_cost_prev,
    accessorId: "total_cost",
    format: "currency",
    axis: SALES_AXIS,
    toolTip: "The total amount of money spent on running the campaign not including 3P fees paid by the agency",
  },
  {
    id: "dsp_cost_percent",
    name: "Cost %",
    color: metricColors.cost[0],
    accessor: (d) => d?.total_cost_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "dsp_total_purchases",
    name: "Total Purchases",
    color: metricColors.metric4[4],
    accessor: (d) => d?.total_purchases ?? 0,
    prev_accessor: (d) => d?.total_purchases_prev ?? 0,
    accessorId: "total_purchases",
    chartType: "column",
    format: "number",
    axis: UNITS_AXIS,
    toolTip:
      "Number of times any quantity of a brand product was included in a purchase event, attributed to an ad view or click. Purchase events include new Subscribe & Save subscriptions and video rentals.",
  },
  {
    id: "dsp_total_purchases_percent",
    name: "Total Purchases %",
    color: metricColors.metric4[4],
    accessor: (d) => d?.total_purchases_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "dsp_units",
    name: "Units",
    color: metricColors.units[0],
    accessor: (d) => d?.units_sold,
    prev_accessor: (d) => d?.units_sold_prev,
    accessorId: "units_sold",
    format: "number",
    axis: UNITS_AXIS,
    chartType: "column",
    toolTip:
      "Units of promoted products purchased, attributed to an ad view or click. A single purchase event can include multiple sold units.",
  },
  {
    id: "dsp_units_percent",
    name: "Units %",
    color: metricColors.units[0],
    accessor: (d) => d?.units_sold_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "dsp_units_delta",
    name: "Units (+/-)",
    color: metricColors.units[0],
    accessor: (d) => d?.units_sold_delta,
    format: "number",
    axis: PERCENT_AXIS,
    incremental: true,
  },

  {
    id: "dsp_total_units",
    name: "Total Units",
    color: metricColors.units[0],
    accessor: (d) => d?.total_units_sold,
    prev_accessor: (d) => d?.total_units_sold_prev,
    accessorId: "total_units_sold",
    format: "number",
    axis: UNITS_AXIS,
    chartType: "column",
    toolTip: "The total units sold",
  },
  {
    id: "dsp_total_units_percent",
    name: "Total Units %",
    color: metricColors.units[0],
    accessor: (d) => d?.total_units_sold_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "dsp_total_units_delta",
    name: "Total Units (+/-)",
    color: metricColors.units[0],
    accessor: (d) => d?.total_units_sold_delta,
    format: "number",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "dsp_ctr",
    name: "CTR",
    color: metricColors.metric3[4],
    accessor: (d) => d?.click_thru_rate,
    prev_accessor: (d) => (d?.click_thru_rate_prev ? d?.click_thru_rate_prev : 0),
    accessorId: "click_thru_rate",
    format: "percent",
    chartType: "line",
    axis: RATE_AXIS,
    toolTip: "The percentage of impressions that result in a click.",
    decimalPlaces: 2,
  },
  {
    id: "dsp_ctr_delta",
    name: "CTR Change",
    color: metricColors.metric3[4],
    accessor: (d) => d?.click_thru_rate_delta,
    format: "percent",
    chartType: "line",
    incremental: true,
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
  },
  {
    id: "dsp_total_acos",
    name: "Total ACOS",
    color: metricColors.acos[1],
    accessor: (d) => d?.tacos ?? 0,
    prev_accessor: (d) => d?.tacos_prev ?? 0,
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    toolTip: "The return on ad spend",
    lower: true,
    compareHighlight: "negativeIsGood",
  },
  {
    id: "dsp_total_acos_delta",
    name: "Total ACOS Change",
    color: metricColors.acos[1],
    accessor: (d) => d?.tacos_delta,
    format: "percent",
    decimalPlaces: 1,
    chartType: "line",
    axis: PERCENT_AXIS,
    incremental: true,
    lower: true,
  },
  {
    id: "cost_per_detail_page_view",
    name: "Cost Per Detail Page View",
    color: metricColors.acos[1],
    accessor: (d) => d?.cost_per_detail_page_view,
    prev_accessor: (d) => d?.cost_per_detail_page_view_prev,
    format: "currency",
    decimalPlaces: 2,
    chartType: "line",
    lower: true,
    compareHighlight: "negativeIsGood",
  },

  {
    id: "dsp_total_roas",
    name: "TROAS",
    color: metricColors.roms[3],
    accessor: (d) => d?.troas ?? 0,
    prev_accessor: (d) => d?.troas_prev ?? 0,
    accessorId: "troas",
    format: "number",
    decimalPlaces: 1,
    chartType: "line",
    axis: ROI_AXIS,
    toolTip: "The return on ad spend against total sales",
  },
  {
    id: "dsp_total_roas_delta",
    name: "TROAS Change",
    color: metricColors.roms[3],
    accessor: (d) => d?.troas_delta,
    format: "number",
    decimalPlaces: 1,
    chartType: "line",
    axis: ROI_AXIS,
    incremental: true,
  },
  {
    id: "dsp_total_detail_page_views",
    name: "Detail Page Views",
    color: metricColors.metric2[0],
    accessor: (d) => d?.total_detail_page_view || d?.dsp_total_detail_page_views,
    prev_accessor: (d) => d?.total_detail_page_view_prev,
    format: "number",
    axis: IMPRESSIONS_AXIS,
    toolTip: "Total detail page views",
  },
  {
    id: "dsp_total_detail_page_views_percent",
    name: "Detail Page Views Percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.total_detail_page_view_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    toolTip: "Total detail page views percentage",
  },
  {
    id: "dsp_total_detail_page_view_rate",
    name: "Detail Page Views Rate",
    color: metricColors.metric1[0],
    accessor: (d) => (d?.total_detail_page_view_rate ? d?.total_detail_page_view_rate : 0),
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    toolTip: "The percentage of page views that result in a sale.",
  },
  {
    id: "dsp_total_detail_page_view_rate_delta",
    name: "Detail Page Views Rate Change",
    color: metricColors.metric1[0],
    accessor: (d) => d?.total_detail_page_view_rate_delta,
    format: "percent",
    chartType: "line",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "dsp_total_add_to_cart",
    name: "Total Add to Cart",
    color: metricColors.metric2[0],
    accessor: (d) => d?.total_add_to_cart || d?.dsp_total_add_to_cart,
    prev_accessor: (d) => d?.total_add_to_cart_prev,
    format: "number",
    axis: IMPRESSIONS_AXIS,
    toolTip: "Total add to cart",
  },
  {
    id: "dsp_total_add_to_cart_percent",
    name: "Total Add to Cart Percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.total_add_to_cart_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    toolTip: "Total add to cart percentage",
  },
  {
    id: "dsp_total_add_to_cart_rate",
    name: "Total Add to Cart Rate",
    color: metricColors.metric1[0],
    accessor: (d) => (d?.total_add_to_cart_rate ? d?.total_add_to_cart_rate : 0),
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    toolTip: "The add-to-cart rate.",
  },
  {
    id: "dsp_total_add_to_cart_rate_delta",
    name: "Total Add to Cart Rate Change",
    color: metricColors.metric1[0],
    accessor: (d) => d?.total_add_to_cart_rate_delta,
    format: "percent",
    chartType: "line",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "dsp_new_to_brand_detail_page_views",
    name: "New to Brand Detail Page Views",
    color: metricColors.metric2[0],
    accessor: (d) => d?.new_to_brand_detail_page_view,
    prev_accessor: (d) => d?.new_to_brand_detail_page_view_prev,
    format: "number",
    axis: IMPRESSIONS_AXIS,
    toolTip: "New to brand detail page views",
  },
  {
    id: "dsp_new_to_brand_detail_page_views_percent",
    name: "New to Brand Detail Page Views Percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.new_to_brand_detail_page_view_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    toolTip: "New to brand detail page views percentage",
  },
  {
    id: "dsp_new_to_brand_detail_page_views_rate",
    name: "New to Brand Detail Page Views Rate",
    color: metricColors.metric1[0],
    accessor: (d) => (d?.new_to_brand_detail_page_views_rate ? d?.new_to_brand_detail_page_views_rate : 0),
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    toolTip: "The percentage of new to brand page views.",
  },
  {
    id: "dsp_new_to_brand_detail_page_views_rate_delta",
    name: "New to Brand Detail Page Views Rate Change",
    color: metricColors.metric1[0],
    accessor: (d) => d?.new_to_brand_detail_page_views_rate_delta,
    format: "percent",
    chartType: "line",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "dsp_total_new_to_brand_dpvs",
    name: "New to Brand DPVs",
    color: metricColors.metric2[0],
    accessor: (d) => d?.total_new_to_brand_dpvs,
    prev_accessor: (d) => d?.total_new_to_brand_dpvs_prev,
    format: "number",
    axis: IMPRESSIONS_AXIS,
    toolTip: "New to brand DPVs",
  },
  {
    id: "dsp_total_new_to_brand_dpvs_percent",
    name: "New to Brand DPVs Percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.total_new_to_brand_dpvs_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    toolTip: "New to brand DPVs percentage",
  },
  {
    id: "dsp_total_new_to_brand_dpv_rate",
    name: "New to Brand DPV Rate",
    color: metricColors.metric1[0],
    accessor: (d) => (d?.total_new_to_brand_dpv_rate ? d?.total_new_to_brand_dpv_rate : 0),
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    toolTip: "The percentage of new to brand DPVs.",
  },
  {
    id: "dsp_total_new_to_brand_dpv_rate_delta",
    name: "New to Brand DPV Rate Change",
    color: metricColors.metric1[0],
    accessor: (d) => d?.total_new_to_brand_dpv_rate_delta,
    format: "percent",
    chartType: "line",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "dsp_brand_searches",
    name: "Brand Searches",
    color: metricColors.metric2[0],
    accessor: (d) => d?.brand_searches,
    prev_accessor: (d) => d?.brand_searches_prev,
    format: "number",
    axis: IMPRESSIONS_AXIS,
    toolTip:
      "The number of times a branded keyword was searched on Amazon, based on the brands associated to the featured ASINs in your campaign.",
  },
  {
    id: "dsp_brand_searches_percent",
    name: "New to Brand DPVs Percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.brand_searches_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    toolTip: "Brand searches percentage",
  },
  {
    id: "dsp_brand_searches_rate",
    name: "Brand Searches Rate",
    color: metricColors.metric1[0],
    accessor: (d) => (d?.brand_searches_rate ? d?.brand_searches_rate : 0),
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    toolTip: "The rate of brand searches.",
  },
  {
    id: "dsp_brand_searches_rate_delta",
    name: "Brand Searches Rate Change",
    color: metricColors.metric1[0],
    accessor: (d) => d?.brand_searches_rate_delta,
    format: "percent",
    chartType: "line",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "dsp_base_bid",
    name: "Base Bid",
    color: metricColors.metric1[0],
    accessor: (d) => d?.bid?.baseBid,
    format: "currency",
    chartType: "line",
    toolTip:
      "Also known as media bid. This is the ideal amount you want to pay per thousand impressions for ad inventory. This price isn’t guaranteed, but Amazon optimizes its bidding strategy to try to meet this goal.",
  },
  {
    id: "dsp_ecpm",
    name: "ECPM",
    color: metricColors.acos[0],
    accessor: (d) => d?.ecpm,
    prev_accessor: (d) => d?.ecpm_prev,
    accessorId: "ecpm",
    format: "currency",
    axis: PERCENT_AXIS,
    toolTip: "The total cost per thousand impressions",
    lower: true,
    compareHighlight: "negativeIsGood",
  },
  {
    id: "dsp_ecpm_percent",
    name: "ECPM %",
    color: metricColors.acos[0],
    accessor: (d) => d?.ecpm_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
    lower: true,
    compareHighlight: "negativeIsGood",
  },
  {
    id: "dsp_ecpm_delta",
    name: "ECPM (+/-)",
    color: metricColors.acos[0],
    accessor: (d) => d?.ecpm_delta,
    format: "currency",
    incremental: true,
    axis: PERCENT_AXIS,
    lower: true,
  },
  {
    id: "dsp_viewable_impressions",
    name: "Viewable Impressions",
    color: metricColors.metric2[1],
    accessor: (d) => d?.viewable_impressions ?? 0,
    prev_accessor: (d) => d?.viewable_impressions_prev ?? 0,
    chartType: "column",
    axis: IMPRESSIONS_AXIS,
    format: "number",
  },
  {
    id: "dsp_viewable_impressions_percent",
    name: "Viewable Impressions %",
    color: metricColors.metric2[1],
    accessor: (d) => d?.viewable_impressions_percent,
    axis: PERCENT_AXIS,
    format: "percent",
  },
  {
    id: "dsp_viewability",
    name: "Viewability",
    color: metricColors.metric2[1],
    accessor: (d) => d?.viewability,
    prev_accessor: (d) => d?.viewability_prev,
    axis: PERCENT_AXIS,
    format: "percent",
  },
  {
    id: "dsp_viewability_delta",
    name: "Viewability Change",
    color: metricColors.metric2[1],
    accessor: (d) => d?.viewability_delta,
    axis: PERCENT_AXIS,
    format: "percent",
  },
  {
    id: "dsp_avg_base_bid",
    name: "Base Bid Average",
    color: metricColors.metric1[0],
    accessor: (d) => d?.base_bid ?? null,
    axis: BID_AXIS,
    format: "currency",
  },
  {
    id: "dsp_avg_base_bid_percent",
    name: "Base Bid Average",
    color: metricColors.metric1[0],
    accessor: (d) => d?.base_bid_percent ?? null,
    axis: PERCENT_AXIS,
    format: "percent",
  },

  // AMC metrics start here
  {
    id: "all_users_with_purchase",
    name: "All Users With Purchases",
    color: metricColors.metric1[0],
    accessor: (d) => d?.all_users_with_purchase ?? null,
    format: "number",
  },
  {
    id: "ntb_users",
    name: "New to Brand Users",
    color: metricColors.metric1[0],
    accessor: (d) => d?.ntb_users ?? null,
    format: "number",
  },
  {
    id: "ntb_percentage",
    name: "New to Brand %",
    color: metricColors.metric1[0],
    accessor: (d) => (d?.ntb_percentage ? d.ntb_percentage / 100 : null),
    format: "percent",
  },
  {
    id: "gateway_asin_rank",
    name: "Gateway ASIN Rank",
    color: metricColors.metric1[0],
    accessor: (d) => d?.gateway_asin_rank ?? null,
    format: "number",
  },
  {
    id: "distinct_user_count",
    name: "Distinct User Count",
    color: metricColors.metric1[0],
    accessor: (d) => d?.distinct_user_count ?? null,
    format: "number",
  },
  {
    id: "customer_cnt",
    name: "Customer Count",
    color: metricColors.metric1[0],
    accessor: (d) => d?.customer_cnt ?? null,
    format: "number",
  },
  {
    id: "units_sold",
    name: "Units Sold",
    color: metricColors.metric1[0],
    accessor: (d) => d?.units_sold ?? null,
    format: "number",
  },
  {
    id: "sales",
    name: "Sales",
    color: metricColors.metric1[0],
    accessor: (d) => d?.sales ?? null,
    format: "currency",
  },
  {
    id: "ltv",
    name: "Lifetime Value",
    color: metricColors.metric1[0],
    accessor: (d) => d?.ltv ?? null,
    format: "currency",
  },

  {
    id: "engagement_scope",
    name: "Engagement Scope",
    color: metricColors.metric1[0],
    accessor: (d) => d?.engagement_scope ?? null,
    format: "text",
  },
  {
    id: "campaign",
    name: "Campaign",
    color: metricColors.metric1[0],
    accessor: (d) => d?.campaign ?? null,
    format: "text",
    options: {
      formatter: (v: string) => {
        return v;
      },
    },
  },
  {
    id: "purchases",
    name: "Purchases",
    color: metricColors.metric1[0],
    accessor: (d) => d?.purchases ?? null,
    format: "number",
  },

  {
    id: "ad_product_type",
    name: "Ad Type",
    color: metricColors.metric1[0],
    accessor: (d) => d?.ad_product_type ?? null,
    format: "text",
  },
  {
    id: "users_that_purchased",
    name: "Users that Purchased",
    color: metricColors.metric1[0],
    accessor: (d) => d?.users_that_purchased ?? null,
    format: "number",
  },
  {
    id: "users_that_clicked",
    name: "Users that Clicked",
    color: metricColors.metric1[0],
    accessor: (d) => d?.users_that_clicked ?? null,
    format: "number",
  },
  {
    id: "unique_reach",
    name: "Unique Reach",
    color: metricColors.metric1[0],
    accessor: (d) => d?.unique_reach ?? null,
    format: "number",
  },
  {
    id: "total_clicks",
    name: "Clicks",
    color: metricColors.metric1[0],
    accessor: (d) => d?.total_clicks ?? null,
    format: "number",
  },
  {
    id: "total_impressions",
    name: "Impressions",
    color: metricColors.metric1[0],
    accessor: (d) => d?.total_impressions ?? null,
    format: "number",
  },
  {
    id: "total_spend",
    name: "Total Spend",
    color: metricColors.metric1[0],
    accessor: (d) => d?.total_spend ?? null,
    format: "currency",
  },
  {
    id: "total_product_sales",
    name: "Total Product Sales",
    color: metricColors.metric1[0],
    accessor: (d) => d?.total_product_sales ?? null,
    format: "currency",
  },
  {
    id: "purchase_rate",
    name: "Purchase Rate",
    color: metricColors.metric1[0],
    accessor: (d) => d?.purchase_rate ?? null,
    format: "percent",
  },
  {
    id: "sns_purchase_frequency",
    name: "SNS Purchase Frequency",
    color: metricColors.metric1[0],
    accessor: (d) => d?.sns_purchase_frequency ?? null,
    format: "text",
  },
  {
    id: "total_sns_count",
    name: "Total SNS Count",
    color: metricColors.metric1[0],
    accessor: (d) => d?.total_sns_count ?? null,
    format: "number",
  },
  {
    id: "percent_total_sns",
    name: "Total SNS %",
    color: metricColors.metric1[0],
    accessor: (d) => d?.percent_total_sns ?? null,
    format: "percent",
  },
  {
    id: "conversions",
    name: "Conversions",
    color: metricColors.metric1[0],
    accessor: (d) => d?.conversions ?? null,
    format: "number",
  },
  {
    id: "roas",
    name: "ROAS",
    color: metricColors.metric1[0],
    accessor: (d) => d?.roas ?? null,
    format: "number",
  },
  {
    id: "user_reach",
    name: "User Reach",
    color: metricColors.metric1[0],
    accessor: (d) => d?.user_reach ?? null,
    format: "number",
  },
  {
    id: "users_that_converted",
    name: "Users that Converted",
    color: metricColors.metric1[0],
    accessor: (d) => d?.users_that_converted ?? null,
    format: "number",
  },
  {
    id: "ad_sales",
    name: "Ad Sales",
    color: metricColors.metric1[0],
    accessor: (d) => d?.ad_sales ?? null,
    format: "currency",
  },
  {
    id: "ad_spend",
    name: "Ad Spend",
    color: metricColors.metric1[0],
    accessor: (d) => d?.ad_spend ?? null,
    format: "currency",
  },
  {
    id: "frequency_bucket",
    name: "Impressions per User",
    color: metricColors.metric1[0],
    accessor: (d) => d?.frequency_bucket ?? null,
    format: "number",
  },
  {
    id: "users_in_bucket",
    name: "Users in Bucket",
    color: metricColors.metric1[0],
    accessor: (d) => d?.users_in_bucket ?? null,
    format: "number",
  },
  {
    id: "impressions_in_bucket",
    name: "Impressions in Bucket",
    color: metricColors.metric1[0],
    accessor: (d) => d?.impressions_in_bucket ?? null,
    format: "number",
  },
  {
    id: "percent_of_total_users",
    name: "Percent of Total Users",
    color: metricColors.metric1[0],
    accessor: (d) => d?.percent_of_total_users ?? null,
    format: "percent",
  },
  {
    id: "lead_asin",
    name: "Lead Asin",
    color: metricColors.metric1[0],
    accessor: (d) => d?.lead_asin ?? null,
    format: "text",
  },
  {
    id: "lead_asin_purchases",
    name: "Lead Asin Purchases",
    color: metricColors.metric1[0],
    accessor: (d) => d?.lead_asin_purchases ?? null,
    format: "number",
  },
  {
    id: "dist_lead_asin_purchased_user_count",
    name: "Distinct User Lead ASIN Purchases",
    color: metricColors.metric1[0],
    accessor: (d) => d?.dist_lead_asin_purchased_user_count ?? null,
    format: "number",
  },
  {
    id: "overlap_asin",
    name: "Overlap ASIN",
    color: metricColors.metric1[0],
    accessor: (d) => d?.overlap_asin ?? null,
    format: "text",
  },
  {
    id: "dist_lead_and_overlap_asin_purchased_user_count",
    name: "Distinct User Lead and Overlap ASIN Purchases",
    color: metricColors.metric1[0],
    accessor: (d) => d?.dist_lead_and_overlap_asin_purchased_user_count ?? null,
    format: "number",
  },
  {
    id: "user_overlap",
    name: "User Overlap",
    color: metricColors.metric1[0],
    accessor: (d) => d?.user_overlap ?? null,
    format: "percent",
  },
  {
    id: "campaign_id_string",
    name: "Campaign ID",
    color: metricColors.metric1[0],
    accessor: (d) => d?.campaign_id_string ?? null,
    format: "text",
  },
  {
    id: "time_to_conversion",
    name: "Time to Conversion",
    color: metricColors.metric1[0],
    accessor: (d) => d?.time_to_conversion ?? null,
    format: "text",
  },
  {
    id: "total_brand_purchases",
    name: "Total Brand Purchases",
    color: metricColors.metric1[0],
    accessor: (d) => d?.total_brand_purchases ?? null,
    format: "number",
  },
  {
    id: "< 1 Min",
    name: "< 1 Min",
    color: metricColors.metric1[0],
    accessor: (d) => d["< 1 Min"] ?? 0,
    format: "number",
  },
  {
    id: "From 1min - 10min",
    name: "From 1min - 10min",
    color: metricColors.metric1[0],
    accessor: (d) => d["From 1min - 10min"] ?? 0,
    format: "number",
  },
  {
    id: "From 10min - 30min",
    name: "From 10min - 30min",
    color: metricColors.metric1[0],
    accessor: (d) => d["From 10min - 30min"] ?? 0,
    format: "number",
  },
  {
    id: "From 30min - 1h",
    name: "From 30min - 1h",
    color: metricColors.metric1[0],
    accessor: (d) => d["From 30min - 1h"] ?? 0,
    format: "number",
  },
  {
    id: "From 1h - 2h",
    name: "From 1h - 2h",
    color: metricColors.metric1[0],
    accessor: (d) => d["From 1h - 2h"] ?? 0,
    format: "number",
  },
  {
    id: "From 2h - 12h",
    name: "From 2h - 12h",
    color: metricColors.metric1[0],
    accessor: (d) => d["From 2h - 12h"] ?? 0,
    format: "number",
  },
  {
    id: "From 12h - 24h",
    name: "From 12h - 24h",
    color: metricColors.metric1[0],
    accessor: (d) => d["From 12h - 24h"] ?? 0,
    format: "number",
  },
  {
    id: "From 1 - 3 Days",
    name: "From 1 - 3 Days",
    color: metricColors.metric1[0],
    accessor: (d) => d["From 1 - 3 Days"] ?? 0,
    format: "number",
  },
  {
    id: "From 3 - 5 Days",
    name: "From 3 - 5 Days",
    color: metricColors.metric1[0],
    accessor: (d) => d["From 3 - 5 Days"] ?? 0,
    format: "number",
  },
  {
    id: "From 5 - 7 Days",
    name: "From 5 - 7 Days",
    color: metricColors.metric1[0],
    accessor: (d) => d["From 5 - 7 Days"] ?? 0,
    format: "number",
  },
  {
    id: "More than 7 Days",
    name: "More than 7 Days",
    color: metricColors.metric1[0],
    accessor: (d) => d["More than 7 Days"] ?? 0,
    format: "number",
  },
  {
    id: "advertiser",
    name: "Advertiser",
    color: metricColors.metric1[0],
    accessor: (d) => d?.advertiser ?? null,
    format: "text",
  },
  {
    id: "campaign_id",
    name: "Campaign ID",
    color: metricColors.metric1[0],
    accessor: (d) => d?.campaign_id ?? null,
    format: "text",
  },
  {
    id: "ntb_conversions",
    name: "NTB Conversions",
    color: metricColors.metric1[0],
    accessor: (d) => d?.ntb_conversions ?? null,
    format: "number",
  },
  {
    id: "non_ntb_conversions",
    name: "Recurring Conversions",
    color: metricColors.metric1[0],
    accessor: (d) => d?.non_ntb_conversions ?? null,
    format: "number",
  },
  {
    id: "ntb_conversions_pct",
    name: "NTB Conversions Percentage",
    color: metricColors.metric1[0],
    accessor: (d) => d?.ntb_conversions_pct ?? null,
    format: "percent",
  },
  {
    id: "ntb_units",
    name: "NTB Units",
    color: metricColors.metric1[0],
    accessor: (d) => d?.ntb_units ?? null,
    format: "number",
  },
  {
    id: "non_ntb_units",
    name: "Recurring Units",
    color: metricColors.metric1[0],
    accessor: (d) => d?.non_ntb_units ?? null,
    format: "number",
  },
  {
    id: "ntb_units_pct",
    name: "NTB Units Percentage",
    color: metricColors.metric1[0],
    accessor: (d) => d?.ntb_units_pct ?? null,
    format: "percent",
  },
  {
    id: "ntb_product_sales",
    name: "NTB Product Sales",
    color: metricColors.metric1[0],
    accessor: (d) => d?.ntb_product_sales ?? null,
    format: "number",
  },
  {
    id: "non_ntb_product_sales",
    name: "Recurring Sales",
    color: metricColors.metric1[0],
    accessor: (d) => d?.non_ntb_product_sales ?? null,
    format: "number",
  },
  {
    id: "ntb_product_sales_pct",
    name: "NTB Product Sales Percentage",
    color: metricColors.metric1[0],
    accessor: (d) => d?.ntb_product_sales_pct ?? null,
    format: "percent",
  },
];

const getDSPMetricSpec = (marketPlace: { marketPlace?: string }, id: string) => {
  const metric = metrics.find((m) => m.id === id);

  const formatter = getFormatter(marketPlace, metric?.format ?? "number", metric);
  return {
    ...metric,
    formatter,
    format: metric?.format,
  };
};

const buildDSPMetric = <T,>(
  marketPlace: { marketPlace?: string },
  data: { total?: T } | null | undefined,
  id: string,
  compareId?: string,
) => {
  try {
    const metric = getDSPMetricSpec(marketPlace, id);
    const compare = compareId ? getDSPMetricSpec(marketPlace, compareId) : null;

    if (metric.totalOverrideAccessor) {
      const totalMetric = getDSPMetricSpec(marketPlace, metric.totalOverrideAccessor);

      if (totalMetric.accessor && typeof totalMetric.accessor === "function") {
        metric.overrideValue = totalMetric.accessor(data?.total);
      }
    }

    metric.grouping = METRIC_GROUPING_LOOKUP[id];
    metric.actual = metric.accessor?.(data?.total);
    metric.target = compare ? compare.accessor?.(data?.total) : null;
    metric.targetFormatter = compare ? compare.formatter : undefined;
    return metric;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    throw new Error(`Error building metric ${id}: ${e.message}`, { cause: e });
  }
};

export { getDSPMetricSpec, buildDSPMetric };
